import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Navbar from '../dashboard/components/Navbar';
import Sidebar from '../dashboard/components/Sidebar';
import { useToast } from '../../components/base/ToastContainer';
import { requisicionesService } from '../../services/requisiciones.service';
import ConfirmDialog from '../../components/base/ConfirmDialog';
import RejectionModal from './components/RejectionModal';
import CrearPrincipioActivoModal from './components/CrearPrincipioActivoModal';

interface Item {
    medicamentoId: string;
    medicamento: string;
    cantidad: number;
    precioEstimado: number;
}

export default function RequisicionFormPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const { showToast } = useToast();

    const getUser = () => {
        try {
            const userStored = localStorage.getItem('user');
            if (userStored) {
                return JSON.parse(userStored);
            }
        } catch (e) {
            console.error("Error parsing user from localStorage", e);
        }
        return null;
    };

    const getBodega = () => {
        try {
            const bodegaStored = localStorage.getItem('selectedBodega');
            if (bodegaStored) {
                return JSON.parse(bodegaStored);
            }

            // Si no hay selectedBodega, buscar en el usuario (caso usuario con una sola bodega)
            const userStored = localStorage.getItem('user');
            if (userStored) {
                const user = JSON.parse(userStored);
                if (user.bodegas && user.bodegas.length > 0) {
                    return user.bodegas[0];
                }
            }
        } catch (e) {
            console.error("Error parsing bodega from localStorage", e);
        }
        return null;
    };

    const user = getUser();
    const bodega = getBodega();

    // Initial state from location (if passed from list) or defaults
    const initialData = location.state?.requisicion;

    const [formData, setFormData] = useState({
        numero: initialData?.numero || '',
        fecha: initialData?.fecha ? initialData.fecha.split('T')[0] : new Date().toISOString().split('T')[0],
        solicitante: initialData?.solicitante || user?.name || user?.nombre || user?.username || "Usuario Actual",
        departamento: initialData?.departamento || bodega?.nombre || "Farmacia Principal",
        prioridad: initialData?.prioridad || 'Media',
        estado: initialData?.estado || '',
    });

    // Isolated state for observations to prevent clobbering
    const [observaciones, setObservaciones] = useState(initialData?.observaciones || '');

    const [isEditable, setIsEditable] = useState(true);

    useEffect(() => {
        const canEdit = !id || (formData.estado && formData.estado.toLowerCase() === 'borrador');
        setIsEditable(!!canEdit);
    }, [id, formData.estado]);

    const [items, setItems] = useState<Item[]>([
        { medicamentoId: '', medicamento: '', cantidad: 1, precioEstimado: 0 }
    ]);

    const [medicamentosBusqueda, setMedicamentosBusqueda] = useState<any[]>([]);
    const [busquedaTerm, setBusquedaTerm] = useState('');
    const [showBusqueda, setShowBusqueda] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    useEffect(() => {
        if (id) {
            const loadRequisicion = async () => {
                try {
                    // Always fetch details because list view doesn't have items
                    const data = await requisicionesService.getDetalle(id);

                    // Robust extraction logic
                    let reqData = data;
                    // Unwrap .data if it exists (wrapper)
                    if (reqData && reqData.data) {
                        reqData = reqData.data;
                    }
                    // Unwrap array if it's an array (list)
                    if (Array.isArray(reqData)) {
                        reqData = reqData.length > 0 ? reqData[0] : null;
                    }

                    if (reqData) {
                        setFormData(prev => ({
                            ...prev,
                            numero: reqData.numero || prev.numero,
                            fecha: reqData.fecha ? reqData.fecha.split('T')[0] : prev.fecha,
                            solicitante: reqData.solicitante?.nombre || reqData.solicitante || prev.solicitante,
                            departamento: reqData.bodega?.nombre || reqData.departamento || prev.departamento,
                            prioridad: reqData.prioridad || prev.prioridad,
                            estado: reqData.estado || prev.estado,
                        }));

                        // Set isolated observations (prefer API data, fallback to previous state if API returns nothing)
                        setObservaciones(prev => reqData.observaciones || reqData.observacion || reqData.comentarios || prev);

                        // Handle details
                        // Use the UNWRAPPED reqData for details if it has them, 
                        // OTHERWISE check if the original 'data' was the array of items (legacy check)
                        let detalles = reqData.detalles || reqData.items || [];

                        // Fallback: if details is empty, and original 'data' was an array, assume data IS the details list
                        if (detalles.length === 0 && Array.isArray(data) && data.length > 0) {
                            detalles = data;
                        }

                        if (Array.isArray(detalles) && detalles.length > 0) {
                            setItems(detalles.map((d: any) => ({
                                medicamentoId: d.medicamento_id || d.medicamento?.id || '',
                                medicamento: d.medicamento?.nombre || d.medicamento_nombre || d.medicamento || '',
                                cantidad: Number(d.cantidad) || 0,
                                precioEstimado: Number(d.precio_estimado || d.precio) || 0
                            })));
                        }
                    }
                } catch (error) {
                    console.error('Error loading requisicion:', error);
                    showToast('Error al cargar la requisición', 'error');
                }
            };
            loadRequisicion();
        }
    }, [id]);


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleItemChange = (index: number, field: keyof Item, value: string | number) => {
        const newItems = [...items];
        // @ts-ignore
        newItems[index][field] = value;
        setItems(newItems);
    };

    const addItem = () => {
        setItems([...items, { medicamentoId: '', medicamento: '', cantidad: 1, precioEstimado: 0 }]);
    };

    const removeItem = (index: number) => {
        if (items.length > 1) {
            setItems(items.filter((_, i) => i !== index));
        }
    };

    const handleSearchMedicamento = async (term: string, index: number) => {
        setBusquedaTerm(term);
        setActiveIndex(index);
        handleItemChange(index, 'medicamento', term);

        if (term.length > 2) {
            try {
                // Assuming medicamentosService.search exists and searches by name or code
                const results = await requisicionesService.getPrincipiosActivos(term);
                setMedicamentosBusqueda(results);
                setShowBusqueda(true);
            } catch (error) {
                console.error("Error searching medications", error);
            }
        } else {
            setMedicamentosBusqueda([]);
            setShowBusqueda(false);
        }
    };

    const selectMedicamento = (medicamento: any) => {
        if (activeIndex !== null) {
            const newItems = [...items];
            newItems[activeIndex] = {
                ...newItems[activeIndex],
                medicamentoId: medicamento.id_sustancia || '',
                medicamento: medicamento.nombre_sustancia,
                precioEstimado: 0 // Will need manual input since we only get name
            };
            setItems(newItems);
            setShowBusqueda(false);
            setBusquedaTerm('');
            setActiveIndex(null);
            setMedicamentosBusqueda([]);
        }
    };

    const handlePrincipioActivoCreated = (data: any) => {
        if (activeIndex !== null) {
            const newItems = [...items];
            newItems[activeIndex] = {
                ...newItems[activeIndex],
                medicamentoId: data.id_sustancia || data.id || '',
                medicamento: data.nombre_sustancia || data.nombre || '',
                precioEstimado: 0
            };
            setItems(newItems);
            setActiveIndex(null);
        }
    };

    const calculateTotal = () => {
        return items.reduce((sum, item) => sum + (item.cantidad * item.precioEstimado), 0);
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            const payload = buildPayload();
            // "Grabar" calls createBorrador (Draft Update)
            await requisicionesService.createBorrador(payload);
            showToast('Requisición actualizada exitosamente', 'success');
            navigate('/compras');
        } catch (error: any) {
            console.error('Error updating requisition:', error);
            showToast(error.message || 'Error al actualizar la requisición', 'error');
        }
    };

    const handleSendApproval = async () => {
        if (!validateForm()) return;

        try {
            const payload = buildPayload();
            // "Enviar para aprobación" calls create (Submit)
            await requisicionesService.create(payload);
            showToast('Requisición enviada para aprobación exitosamente', 'success');
            navigate('/compras');
        } catch (error: any) {
            console.error('Error sending for approval:', error);
            showToast(error.message || 'Error al enviar para aprobación', 'error');
        }
    };

    const validateForm = () => {
        if (!bodega?.idbodega) {
            showToast('No se ha seleccionado una bodega', 'error');
            return false;
        }
        if (!user?.id) {
            showToast('No se ha identificado el usuario solicitante', 'error');
            return false;
        }
        return true;
    };

    const buildPayload = () => {
        const total = calculateTotal();
        return {
            id_requisicion: id || null,
            idbodega: bodega?.idbodega || '',
            fecha: formData.fecha,
            solicitante_id: user?.id || '',
            prioridad: formData.prioridad,
            observaciones: observaciones,
            total: total,
            detalles: items.map(item => ({
                medicamento_id: item.medicamentoId,
                cantidad: item.cantidad,
                precio_estimado: item.precioEstimado,
                subtotal: item.cantidad * item.precioEstimado
            }))
        };
    };

    const handleCreateAndSend = async () => {
        if (!validateForm()) return;

        try {
            const payload = buildPayload();
            // Create AND Send uses the main CREATE endpoint
            await requisicionesService.create(payload);
            showToast('Requisición enviada para aprobación exitosamente', 'success');
            navigate('/compras');
        } catch (error: any) {
            console.error('Error creating and sending requisition:', error);
            showToast(error.message || 'Error al enviar la requisición', 'error');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            const payload = buildPayload();
            // Standard "Guardar" calls createBorrador (Draft)
            await requisicionesService.createBorrador(payload);
            showToast('Borrador de requisición guardado exitosamente', 'success');
            navigate('/compras');
        } catch (error: any) {
            console.error('Error creating requisition draft:', error);
            showToast(error.message || 'Error al guardar el borrador', 'error');
        }
    };

    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [showApproveConfirm, setShowApproveConfirm] = useState(false);

    // Check if user is admin - simplified check based on typically available data
    // Ideally this should use authService.hasRole('administrador') but we need to ensure it's available
    const isAdmin = (user?.rol && user.rol.toLowerCase() === 'administrador') ||
        (user?.role && user.role.toLowerCase() === 'administrador') ||
        (user?.roles && user.roles.some((r: string) => r.toLowerCase() === 'administrador'));

    const handleApproveClick = () => {
        setShowApproveConfirm(true);
    };

    const handleRejectClick = () => {
        setShowRejectionModal(true);
    };

    const handleConfirmApprove = async () => {
        try {
            await requisicionesService.aprobar(id!);
            showToast('Requisición aprobada exitosamente', 'success');
            navigate('/compras');
        } catch (error: any) {
            console.error('Error approving requisition:', error);
            showToast(error.message || 'Error al aprobar la requisición', 'error');
        } finally {
            setShowApproveConfirm(false);
        }
    };

    const handleConfirmReject = async (motivo: string) => {
        try {
            await requisicionesService.rechazar(id!, motivo);
            showToast('Requisición rechazada exitosamente', 'success');
            navigate('/compras');
        } catch (error: any) {
            console.error('Error rejecting requisition:', error);
            showToast(error.message || 'Error al rechazar la requisición', 'error');
        } finally {
            setShowRejectionModal(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
            <Navbar />
            <div className="flex pt-20">
                <Sidebar />
                <main className="flex-1 p-8 transition-all duration-300" style={{ marginLeft: 'var(--sidebar-width, 256px)' }}>
                    <div className="w-full">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                            <div>
                                <h1 className="text-3xl font-bold text-slate-800 mb-2">
                                    {id ? 'Editar Requisición' : 'Nueva Requisición'}
                                </h1>
                                <p className="text-slate-600">Gestione la solicitud de medicamentos e insumos</p>
                            </div>
                            <div className="flex gap-2">
                                {/* Admin Actions */}
                                {id && isAdmin && (formData.estado === 'Pendiente' || formData.estado === 'En Proceso') && (
                                    <>
                                        <button
                                            onClick={handleApproveClick}
                                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
                                        >
                                            <i className="ri-check-line"></i>
                                            Aprobar
                                        </button>
                                        <button
                                            onClick={handleRejectClick}
                                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
                                        >
                                            <i className="ri-close-line"></i>
                                            Rechazar
                                        </button>
                                    </>
                                )}
                                <button
                                    onClick={() => navigate('/compras')}
                                    className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium flex items-center gap-2"
                                >
                                    <i className="ri-arrow-left-line"></i>
                                    Volver
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                            {/* ... (existing inputs remain unchanged) ... */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Número de Requisición
                                    </label>
                                    <input
                                        type="text"
                                        name="numero"
                                        value={formData.numero}
                                        readOnly
                                        className="w-full px-4 py-2 text-sm border border-slate-200 bg-slate-50 text-slate-500 rounded-lg focus:outline-none cursor-not-allowed"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Fecha *
                                    </label>
                                    <input
                                        type="date"
                                        name="fecha"
                                        value={formData.fecha}
                                        onChange={handleInputChange}
                                        required
                                        disabled={!isEditable}
                                        className={`w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${!isEditable ? 'bg-slate-50 text-slate-500' : ''}`}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Prioridad *
                                    </label>
                                    <select
                                        name="prioridad"
                                        value={formData.prioridad}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        className={`w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white ${!isEditable ? 'bg-slate-50 text-slate-500' : ''}`}
                                    >
                                        <option value="Baja">Baja</option>
                                        <option value="Media">Media</option>
                                        <option value="Alta">Alta</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Solicitante *
                                    </label>
                                    <input
                                        type="text"
                                        name="solicitante"
                                        value={formData.solicitante}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Punto Físico
                                    </label>
                                    <input
                                        type="text"
                                        name="departamento"
                                        value={formData.departamento}
                                        readOnly
                                        className="w-full px-4 py-2 text-sm border border-slate-200 bg-slate-50 text-slate-500 rounded-lg focus:outline-none cursor-not-allowed"
                                    />
                                </div>
                            </div>

                            <div className="mb-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
                                <div className="flex items-center justify-between mb-6">
                                    <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                        <i className="ri-list-check text-teal-600"></i>
                                        Detalle de Requisición
                                    </h3>
                                    {isEditable && (
                                        <button
                                            type="button"
                                            onClick={addItem}
                                            className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm font-medium cursor-pointer shadow-sm flex items-center gap-2"
                                        >
                                            <i className="ri-add-line"></i>
                                            Agregar Item
                                        </button>
                                    )}
                                </div>

                                <div className="space-y-4">
                                    {items.map((item, index) => (
                                        <div key={index} className="flex flex-col md:flex-row gap-4 items-start bg-white p-4 rounded-lg border border-slate-200 shadow-sm relative">
                                            <div className="flex-1 w-full relative">
                                                <label className="block text-xs font-medium text-slate-600 mb-1">
                                                    Principio Activo <span className="text-red-500">*</span>
                                                </label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={item.medicamento}
                                                        onChange={(e) => handleSearchMedicamento(e.target.value, index)}
                                                        required
                                                        disabled={!isEditable}
                                                        className={`w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${!isEditable ? 'bg-slate-50 text-slate-500' : ''}`}
                                                        placeholder="Buscar principio activo..."
                                                    />
                                                    {isEditable && (
                                                        <button
                                                            type="button"
                                                            onClick={async () => {
                                                                setActiveIndex(index);
                                                                if (item.medicamento && item.medicamento.length > 2) {
                                                                    try {
                                                                        const results = await requisicionesService.getPrincipiosActivos(item.medicamento);
                                                                        setMedicamentosBusqueda(results);
                                                                        setShowBusqueda(true);
                                                                    } catch (error) {
                                                                        console.error("Error searching active principles", error);
                                                                    }
                                                                } else {
                                                                    showToast('Ingrese al menos 3 caracteres para buscar', 'info');
                                                                }
                                                            }}
                                                            className="px-3 py-2 bg-slate-100 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-200 transition-colors"
                                                            title="Buscar"
                                                        >
                                                            <i className="ri-search-line"></i>
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setActiveIndex(index);
                                                            setShowCreateModal(true);
                                                        }}
                                                        className="px-3 py-2 bg-teal-50 text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors"
                                                        title="Crear Nuevo Principio"
                                                    >
                                                        <i className="ri-add-line"></i>
                                                    </button>
                                                </div>
                                                {/* Resultados de búsqueda flotantes */}
                                                {showBusqueda && activeIndex === index && medicamentosBusqueda.length > 0 && (
                                                    <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 flex flex-col">
                                                        <div className="flex justify-between items-center px-4 py-2 border-b border-slate-100 bg-slate-50 sticky top-0">
                                                            <span className="text-xs font-semibold text-slate-500">Resultados</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => { setShowBusqueda(false); setActiveIndex(null); }}
                                                                className="text-slate-400 hover:text-red-500 transition-colors"
                                                            >
                                                                <i className="ri-close-line text-lg"></i>
                                                            </button>
                                                        </div>
                                                        <div className="overflow-y-auto flex-1">
                                                            {medicamentosBusqueda.map((med: any, idx) => (
                                                                <button
                                                                    key={`${med.id_sustancia}-${idx}`}
                                                                    type="button"
                                                                    onClick={() => selectMedicamento(med)}
                                                                    className="w-full text-left px-4 py-2 hover:bg-teal-50 text-sm border-b border-slate-50 last:border-0"
                                                                >
                                                                    <div className="font-medium text-slate-800">{med.nombre_sustancia}</div>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="w-full md:w-32">
                                                <label className="block text-xs font-medium text-slate-600 mb-1">
                                                    Cantidad <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={item.cantidad}
                                                    onChange={(e) => handleItemChange(index, 'cantidad', e.target.value === '' ? ('' as any) : parseInt(e.target.value))}
                                                    required
                                                    disabled={!isEditable}
                                                    className={`w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${!isEditable ? 'bg-slate-50 text-slate-500' : ''}`}
                                                />
                                            </div>

                                            <div className="w-full md:w-40">
                                                <label className="block text-xs font-medium text-slate-600 mb-1">
                                                    Precio Est.
                                                </label>
                                                <div className="relative">
                                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={item.precioEstimado}
                                                        onChange={(e) => handleItemChange(index, 'precioEstimado', e.target.value === '' ? ('' as any) : parseFloat(e.target.value))}
                                                        required
                                                        disabled={!isEditable}
                                                        className={`w-full pl-7 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${!isEditable ? 'bg-slate-50 text-slate-500' : ''}`}
                                                    />
                                                </div>
                                            </div>

                                            <div className="w-full md:w-40">
                                                <label className="block text-xs font-medium text-slate-600 mb-1">
                                                    Subtotal
                                                </label>
                                                <div className="px-3 py-2 text-sm bg-slate-100 text-slate-600 rounded-lg border border-slate-200 font-medium text-right">
                                                    ${(item.cantidad * item.precioEstimado).toLocaleString()}
                                                </div>
                                            </div>

                                            {items.length > 1 && isEditable && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(index)}
                                                    className="w-8 h-8 mt-6 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                                                    title="Eliminar item"
                                                >
                                                    <i className="ri-delete-bin-line text-lg"></i>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-6 flex justify-end">
                                    <div className="bg-teal-50 p-4 rounded-lg border border-teal-100 min-w-[200px]">
                                        <p className="text-xs text-teal-600 font-medium mb-1">Total Estimado</p>
                                        <p className="text-2xl font-bold text-teal-800">${calculateTotal().toLocaleString()}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                                <label className="block text-sm font-medium text-slate-800 mb-2">
                                    Observaciones
                                </label>
                                <textarea
                                    rows={4}
                                    name="observaciones"
                                    value={observaciones}
                                    onChange={(e) => setObservaciones(e.target.value)}
                                    className={`w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none ${!isEditable ? 'bg-slate-100' : 'bg-white'}`}
                                    placeholder="Observaciones adicionales..."
                                    disabled={!isEditable}
                                ></textarea>
                            </div>

                            <div className="flex items-center justify-end gap-4 pt-4 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => navigate('/compras')}
                                    className="px-6 py-2.5 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors text-sm font-medium cursor-pointer"
                                >
                                    Cancelar
                                </button>

                                {isEditable && !id && (
                                    <>
                                        <button
                                            type="submit"
                                            className="px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm font-medium cursor-pointer shadow-lg shadow-teal-500/30 flex items-center gap-2"
                                        >
                                            <i className="ri-save-line text-lg"></i>
                                            Guardar Requisición
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCreateAndSend}
                                            className="px-6 py-2.5 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors text-sm font-medium cursor-pointer shadow-lg shadow-blue-400/30 flex items-center gap-2"
                                        >
                                            <i className="ri-send-plane-line text-lg"></i>
                                            Enviar para aprobación
                                        </button>
                                    </>
                                )}

                                {isEditable && id && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={handleSave}
                                            className="px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium cursor-pointer shadow-lg shadow-blue-500/30 flex items-center gap-2"
                                        >
                                            <i className="ri-save-2-line text-lg"></i>
                                            Grabar
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSendApproval}
                                            className="px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm font-medium cursor-pointer shadow-lg shadow-teal-500/30 flex items-center gap-2"
                                        >
                                            <i className="ri-send-plane-line text-lg"></i>
                                            Enviar para aprobación
                                        </button>
                                    </>
                                )}
                            </div>
                        </form>
                    </div>
                </main>
            </div >

            <ConfirmDialog
                isOpen={showApproveConfirm}
                title="Aprobar Requisición"
                message="¿Está seguro que desea aprobar esta requisición?"
                confirmText="Aprobar"
                type="info"
                onConfirm={handleConfirmApprove}
                onCancel={() => setShowApproveConfirm(false)}
            />

            <RejectionModal
                isOpen={showRejectionModal}
                onClose={() => setShowRejectionModal(false)}
                onConfirm={handleConfirmReject}
            />

            <CrearPrincipioActivoModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={handlePrincipioActivoCreated}
            />
        </div >
    );
}
